<template>
    <div class="choose-exam">
        <!-- @todo update uikit to support nest-server types -->
        <BundleSearch
            :bundles="bundles as Study.Class.BundleJSON[]"
            :exam-metadata-by-id="examMetadata as Record<string, Study.Class.ExamMetadataJSON>"
            @select="handleSelectExam"
            @selectBundle="handleSelectBundle"
            @search="handleSearch"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'

export type TBundleExam = (Study.Class.ExamMetadataJSON | ClientExam)
    & { bundle: Study.Class.BundleJSON | ClientBundle }

const { BundleSearch } = UIKit

const props = withDefaults(
    defineProps<{
        hideExamSearchBar?: boolean
    }>(),
    {
        hideExamSearchBar: false,
    }
)

const emit = defineEmits<{
    select: [exam: TBundleExam]
    selectBundle: [bundle: Study.Class.BundleJSON | ClientBundle]
    search: [searchString: string]
}>()

const route = useRoute()

const bundles = computed(() => {
    return [ ...bundleModule.getters.getBundles() ]
        .filter(bundle => !bundleModule.getters.isBundleDisabled(bundle.name))
        .sort((a, b) => a.name.localeCompare(b.name))
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getMostRecentExamMetadataById({ excludeDiscontinued: true })
})

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    // Click the bundle that matches query param, if present and valid
    const initialBundleId = route.query.bundleId
    if (initialBundleId && typeof initialBundleId === 'string') {
        const bundleIndex = bundles.value.findIndex(b => b.objectId === initialBundleId)
        if (bundleIndex !== -1) {
            const bundleEl = document.querySelectorAll<HTMLElement>('.uikit-bundle-list__item')[bundleIndex]
            bundleEl?.click()
        }
    }

    // We want to hide the exam search bar for the Marketing Scope Onboarding project
    if (props.hideExamSearchBar) {
        const uikitExamSearchBar = document.querySelector('.uikit-exam-search__search') as HTMLElement
        if (uikitExamSearchBar) {
            uikitExamSearchBar.style.display = 'none'
        }
    }
})

const handleSelectExam = (exam: TBundleExam) => {
    emitSelect(exam)
}

const handleSelectBundle = (bundle: Study.Class.BundleJSON | ClientBundle) => {
    emitSelectBundle(bundle)
}

const handleSearch = (search: string) => {
    emitSearch(search)
}

const emitSelect = (exam: TBundleExam) => {
    emit('select', exam)
}

const emitSelectBundle = (bundle: Study.Class.BundleJSON | ClientBundle) => {
    emit('selectBundle', bundle)
}

const emitSearch = (searchString: string) => {
    emit('search', searchString)
}
</script>

<style lang="scss" scoped>
.choose-exam {
    padding-top: 23px;
    max-width: 885px;
    width: 100%;
    height: calc(100% - 80px);
    margin: 0 auto -30px;
    box-sizing: border-box;

    @include breakpoint(brown-bear) {
        max-width: 657px;
    }

    @include breakpoint(black-bear) {
        max-width: 356px;
        height: auto;
    }
}
</style>
